import React, { FC, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { AnyAction } from 'redux'
import {
    Drawer,
    Form,
    Button,
    Col,
    Row,
    Input,
    Space,
    Divider
} from 'antd'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import PrefixService from "../../../services/prefix"
import { requestAuthInit } from '../../../store/auth/auth.actions'
import { truewalletConfirmOTPParams, truewalletRequestOTPParams } from '../../../@types/truewallet'
import { ThunkDispatch } from '../../../@types/store'
import PatternNumber from '../../../utils/PatternNumber'
import { FuncFormattedMessage } from '../../../utils/FuncFormattedMessage'
import FormattedMessage from '../../Elements/FormattedMessage'

interface AddTruewalletProps {
    close: () => void
    isOpen: boolean
}

const AddTruewallet: FC<AddTruewalletProps> = ({ close, isOpen }) => {
    const [form] = Form.useForm();
    const title = FuncFormattedMessage("truemoneywallet.add", "เพิ่มบัญชีทรูมันนี่วอลเล็ท")
    const [btnLoading, setBtnLoading] = useState(false)
    const dispatch: ThunkDispatch<AnyAction> = useDispatch()

    const text_confirm = FuncFormattedMessage("confirm", "ตกลง")
    const text_cancel = FuncFormattedMessage("cancel", "ยกเลิก")

    const text_please_check_pin = FuncFormattedMessage("truemoneywallet.input.please_check_pin", "กรุณาตรวจสอบรหัส OTP ที่ได้รับจากระบบทรูมันนี่")
    const text_swal_text = FuncFormattedMessage("truemoneywallet.input.please_pin", "กรุณากรอก OTP")
    const insertData = async (values: truewalletRequestOTPParams) => {
        try {
            const res = await PrefixService.requestTruewalletOTP({
                phone: values.phone,
                password: values.password,
                pin: values.pin
            })
            const otpRef = res.otpRef
            if (otpRef) {
                toast.success(text_please_check_pin)
                Swal.fire({
                    title: 'OTP',
                    text: `${text_swal_text} (Ref:${otpRef})`,
                    input: 'text',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    cancelButtonText: text_cancel,
                    confirmButtonText: text_confirm,
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        try {
                            const res: any = await PrefixService.confirmTruewalletOTP({
                                phone: values.phone,
                                password: values.password,
                                pin: values.pin,
                                otpRef: otpRef,
                                otpCode: result.value
                            } as truewalletConfirmOTPParams)
                            setBtnLoading(false)
                            dispatch(requestAuthInit(false))
                            toast.success(res.message)
                            close()
                        } catch ({ message }) {
                            toast.error(message as string)
                            setBtnLoading(false)
                            form.setFieldsValue({ 'otpRef': "" })
                        } finally {
                            setBtnLoading(false)
                            form.setFieldsValue({ 'otpRef': "" })
                        }
                    } else {
                        setBtnLoading(false)
                        form.setFieldsValue({ 'otpRef': "" })
                    }
                })
            } else {
                setBtnLoading(false)
                form.setFieldsValue({ 'otpRef': "" })
            }
        } catch ({ message }) {
            toast.error(message as string)
            setBtnLoading(false)
        }
    }

    const handleFormSubmit = () => {
        form.validateFields()
            .then((values: truewalletRequestOTPParams) => {
                setBtnLoading(true)
                insertData(values)
            })
            .catch((errorInfo) => { });
    };


    useEffect(() => {
        if (form && isOpen) {
            form.resetFields();
        }
    }, [form, isOpen]);

    const text_tel = FuncFormattedMessage("tel", "เบอร์โทรศัพท์")
    const text_password = FuncFormattedMessage("password", "รหัสผ่าน")

    return (
        <>
            <Drawer
                title={title}
                onClose={close}
                visible={isOpen}
                bodyStyle={{ paddingBottom: 80 }}
                width={global.window.innerWidth <= 1200 ? global.window.innerWidth : global.window.innerWidth * .4}
                extra={
                    <Space>
                        <Button
                            onClick={handleFormSubmit}
                            type="primary"
                            loading={btnLoading}
                            size="large">
                            <i className="fa-solid fa-save me-2"></i>
                            <FormattedMessage id="save" defaultMessage="บันทึก" />
                        </Button>
                    </Space>
                }>
                <Form
                    form={form}
                    name="AddTruewallet"
                    layout="vertical"
                    hideRequiredMark>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="phone"
                                label={text_tel}
                                rules={[
                                    { required: true, message: <FormattedMessage id="please-enter-your-phone-number" defaultMessage="กรุณากรอกเบอร์โทรศัพท์" /> },
                                    { pattern: PatternNumber, message: <FormattedMessage id="please-enter-numbers-only" defaultMessage="กรุณากรอกเป็นตัวเลขเท่านั้น" /> }
                                ]}
                                hasFeedback>
                                <Input placeholder={text_tel} maxLength={10} showCount inputMode="tel" autoComplete="new-phone" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="password"
                                label={text_password}
                                rules={[{ required: true, message: <FormattedMessage id="message-password" defaultMessage="กรุณากรอกรหัสผ่าน" /> }]}
                                hasFeedback>
                                <Input.Password placeholder={text_password} inputMode="text" autoComplete="new-password" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="pin"
                                label="PIN"
                                rules={[
                                    { required: true, message: <FormattedMessage id="truemoneywallet.input.please_pin" defaultMessage="กรุณากรอก PIN" /> },
                                    { pattern: PatternNumber, message: <FormattedMessage id="please-enter-numbers-only" defaultMessage="กรุณากรอกเป็นตัวเลขเท่านั้น" /> }
                                ]}
                                hasFeedback>
                                <Input.Password placeholder="PIN" maxLength={6} showCount inputMode="numeric" autoComplete="new-pin" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider />
                    <div className="drawer-actions">
                        <Space>
                            <Button type="primary" htmlType="submit" disabled={btnLoading} onClick={handleFormSubmit} size="large">
                                <i className="fa-solid fa-save me-2"></i> <FormattedMessage id="save" defaultMessage="บันทึก" />
                            </Button>
                            <Button onClick={close} size="large">
                                <FormattedMessage id="cancel" defaultMessage="ยกเลิก" />
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Drawer>
        </>
    )
}
export default AddTruewallet

